import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { apiFetch } from "../../utils/api";

export function ResetPasswordPage() {
  usePageTitle("\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815");
  const m = useIsMobile();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ message: string; no_notification?: boolean } | null>(null);
  const st = styles(m);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiFetch<{ success: boolean; message: string; no_notification?: boolean }>(
        "/api/auth/reset-password-request",
        {
          method: "POST",
          body: JSON.stringify({ phone: phone.replace(/\D/g, "") }),
        },
      );
      setResult(res);
    } catch (err: any) {
      setError(err.message || "\uC694\uCCAD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={st.page}>
      <AppHeader />
      <div style={st.container}>
        <div style={st.card}>
          <div style={st.logoArea}>
            <div style={st.icon}>{"\uD83D\uDD13"}</div>
            <h1 style={st.logo}>
              <span style={{ color: "var(--lt-gold)" }}>{"\uB545"}</span>{"\uC2E0\uB8B0"}
            </h1>
            <div style={st.badge}>PASSWORD RESET</div>
            <p style={st.subtitle}>{"\uBE44\uBC00\uBC88\uD638 \uC7AC\uC124\uC815"}</p>
          </div>

          {!result ? (
            <form onSubmit={handleSubmit} style={st.form}>
              <p style={st.desc}>
                {"\uAC00\uC785 \uC2DC \uB4F1\uB85D\uD55C \uD734\uB300\uD3F0 \uBC88\uD638\uB97C \uC785\uB825\uD558\uC138\uC694.\uC784\uC2DC \uBE44\uBC00\uBC88\uD638\uB97C \uC774\uBA54\uC77C \uB610\uB294 \uCE74\uCE74\uC624 \uC54C\uB9BC\uD1A1\uC73C\uB85C \uBC1C\uC1A1\uD569\uB2C8\uB2E4."}
              </p>
              <div style={st.field}>
                <label style={st.label}>{"\uD734\uB300\uD3F0 \uBC88\uD638"}</label>
                <input
                  type="tel"
                  placeholder="010-0000-0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={st.input}
                  required
                />
              </div>
              {error && <p style={st.error}>{error}</p>}
              <button type="submit" disabled={loading} style={{ ...st.btn, opacity: loading ? 0.6 : 1 }}>
                {loading ? "\uBC1C\uC1A1 \uC911..." : "\uC784\uC2DC \uBE44\uBC00\uBC88\uD638 \uBC1C\uC1A1"}
              </button>
            </form>
          ) : (
            <div style={st.resultBox}>
              {result.no_notification ? (
                <>
                  <div style={st.warnIcon}>{"\u26A0\uFE0F"}</div>
                  <p style={st.resultTitle}>{"\uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD558\uC138\uC694"}</p>
                  <p style={st.resultMsg}>{result.message}</p>
                </>
              ) : (
                <>
                  <div style={st.successIcon}>{"\u2705"}</div>
                  <p style={st.resultTitle}>{"\uBC1C\uC1A1 \uC644\uB8CC"}</p>
                  <p style={st.resultMsg}>{result.message}</p>
                </>
              )}
              <Link to="/login" style={st.backLink}>{"\uB85C\uADF8\uC778\uD654\uBA74\uC73C\uB85C"}</Link>
            </div>
          )}

          {!result && (
            <p style={st.linkText}>
              {"\uAE30\uC5B5\uB098\uC154\uC694?\u00A0"}
              <Link to="/login" style={st.link}>{"\uB85C\uADF8\uC778"}</Link>
            </p>
          )}
        </div>
      </div>
      <AppFooter />
    </div>
  );
}

function styles(m: boolean): Record<string, React.CSSProperties> {
  return {
    page: { minHeight: "100vh", display: "flex", flexDirection: "column" as const, background: "var(--bg-primary)" },
    container: {
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      padding: m ? 16 : 20,
      background: "radial-gradient(ellipse at 50% -10%, rgba(212,168,83,0.07) 0%, transparent 65%)",
    },
    card: {
      width: "100%", maxWidth: 560,
      background: "var(--bg-card)",
      border: "1px solid var(--border-gold, rgba(212,168,83,0.2))",
      borderTop: "2px solid var(--lt-gold)",
      borderRadius: "var(--radius-xl)",
      padding: m ? "32px 20px 28px" : "44px 40px 36px",
      boxShadow: "var(--shadow-gold, 0 0 0 1px rgba(212,168,83,0.15), 0 24px 56px rgba(0,0,0,0.6))",
    },
    logoArea: { textAlign: "center" as const, marginBottom: 28 },
    icon: { fontSize: 30, marginBottom: 12 },
    badge: {
      display: "inline-block", padding: "3px 12px", borderRadius: 20,
      border: "1px solid rgba(212,168,83,0.35)", color: "var(--lt-gold)",
      fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", marginBottom: 8,
    },
    logo: { fontSize: m ? 30 : 36, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 6px" },
    subtitle: { fontSize: 13, color: "var(--text-muted)", marginTop: 0 },
    form: { display: "flex", flexDirection: "column" as const, gap: 16 },
    desc: { fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 },
    field: { display: "flex", flexDirection: "column" as const, gap: 6 },
    label: { fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.5px", textTransform: "uppercase" as const },
    input: {
      padding: "13px 14px", borderRadius: "var(--radius-md)",
      border: "1px solid var(--border-medium)",
      background: "var(--bg-secondary)", color: "var(--text-primary)",
      fontSize: 16, outline: "none",
    },
    error: {
      color: "var(--lt-red)", fontSize: 13, margin: 0,
      padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: "var(--radius-md)",
    },
    btn: {
      padding: "14px", borderRadius: "var(--radius-md)", border: "none",
      background: "linear-gradient(135deg, #B8972E, #D4AF37)",
      color: "#0F172A", fontSize: 16, fontWeight: 700,
      cursor: "pointer", marginTop: 4, letterSpacing: "0.3px",
    },
    resultBox: {
      textAlign: "center" as const,
      padding: "8px 0 16px",
      display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 12,
    },
    successIcon: { fontSize: 40 },
    warnIcon: { fontSize: 40 },
    resultTitle: { fontSize: 18, fontWeight: 700, color: "var(--text-primary)", margin: 0 },
    resultMsg: { fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6, textAlign: "center" as const },
    backLink: {
      display: "inline-block", marginTop: 8,
      padding: "12px 28px", borderRadius: "var(--radius-md)",
      background: "linear-gradient(135deg, #B8972E, #D4AF37)",
      color: "#0F172A", fontWeight: 700, fontSize: 15,
      textDecoration: "none",
    },
    linkText: { textAlign: "center" as const, fontSize: 14, color: "var(--text-muted)", marginTop: 24 },
    link: { color: "var(--lt-gold)", textDecoration: "none", fontWeight: 600 },
  };
}
